/**
 * @file UsersPage.tsx
 * @brief Super Admin → Users page. Wires the data table, filters,
 *        create/edit/change-password dialogs, and toasts together.
 *
 * @details
 * - URL query params drive the filter state so reloads / shareable links
 *   preserve the user's view.
 * - List query is invalidated by every successful mutation, so the table
 *   refreshes automatically.
 * - Profile avatars use first/last initials when no photo URL is present.
 *
 * @dependencies
 * - features/users/api (TanStack Query hooks)
 * - features/users/components/* (dialogs, filters, badges, row actions)
 * - components/data/DataTable
 *
 * @references
 * - BACKEND/src/features/admin/routes.js
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — GridBlock end-to-end; compact columns + row tooltip
 */

import { DockActionButton, PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { PersonPhoto } from "@/components/ui/PersonPhoto";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, UserRoundPlus, Users as UsersIcon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/Button";
import { useOpenCreateFromQuery } from "@/shared/hooks/useOpenCreateFromQuery";
import {
  DataTable,
  type PaginationState,
} from "@/components/data/DataTable";
import type { TableRowGroup } from "@/components/data/groupRowsBy";
import {
  formatDateTime,
  formatFullName,
  formatRelative,
  getInitials,
} from "@/shared/utils/formatters";
import { userCompanyGroupMeta } from "@/shared/utils/tableGroups";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import {
  useUsersGroupRows,
  useUsersGroupSummary,
  useUsersList,
  usersCompanyGroupKey,
} from "./api";
import type { AdminUser, AdminUsersListParams } from "./schemas";
import { ChangePasswordDialog } from "./components/ChangePasswordDialog";
import { CreateUserDialog } from "./components/CreateUserDialog";
import { EditUserDialog } from "./components/EditUserDialog";
import { LoginHistoryDialog } from "./components/LoginHistoryDialog";
import { LoginLocationPill } from "@/shared/components/LoginLocationPill";
import { UserRoleBadge } from "./components/UserRoleBadge";
import { Badge } from "@/components/ui/Badge";
import { UserRowActions } from "./components/UserRowActions";
import { userStatusTimelineMarkerClass } from "./components/userStatusStyles";
import { UserBillingBadge } from "./components/UserBillingBadge";
import { UserNativeLanguageCell } from "./components/UserNativeLanguageCell";
import { UserVoiceEnrollmentBadge } from "./components/UserVoiceEnrollmentBadge";
import { UsersFilters, UsersHeaderFilters } from "./components/UsersFilters";

// ----------------------------------------------------------------------
// Helpers — keep filter state synced to the URL so the page survives
// reloads, deep links, and back-button navigation.
// ----------------------------------------------------------------------
function paramsFromSearch(search: URLSearchParams): AdminUsersListParams {
  const role = search.get("role");
  const status = search.get("status");
  const billing = search.get("billing");
  const nativeLanguage = search.get("lang")?.trim().toLowerCase() || undefined;
  const billingOk =
    billing === "basic" ||
    billing === "subscribed" ||
    billing === "starter" ||
    billing === "pro" ||
    billing === "business"
      ? billing
      : undefined;
  return {
    page: Number(search.get("page")) || 1,
    limit: Number(search.get("limit")) || 25,
    search: search.get("q") || undefined,
    role: role === "user" || role === "admin" || role === "superadmin" ? role : undefined,
    status:
      status === "active" || status === "suspended" || status === "deleted"
        ? status
        : undefined,
    company_id: search.get("company_id") || undefined,
    native_language: nativeLanguage || undefined,
    billing: billingOk,
  };
}

function searchFromParams(p: AdminUsersListParams): URLSearchParams {
  const usp = new URLSearchParams();
  if (p.page && p.page !== 1) usp.set("page", String(p.page));
  if (p.limit && p.limit !== 25) usp.set("limit", String(p.limit));
  if (p.search) usp.set("q", p.search);
  if (p.role) usp.set("role", p.role);
  if (p.status) usp.set("status", p.status);
  if (p.company_id) usp.set("company_id", p.company_id);
  if (p.native_language) usp.set("lang", p.native_language);
  if (p.billing) usp.set("billing", p.billing);
  return usp;
}

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
export function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => paramsFromSearch(searchParams), [searchParams]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [pwTarget, setPwTarget] = useState<AdminUser | null>(null);
  const [historyTarget, setHistoryTarget] = useState<AdminUser | null>(null);

  // Cmd+K “Create user” → /admin/users?create=1 (17-07-2026)
  useOpenCreateFromQuery(setCreateOpen);

  const isGrouped = !params.company_id;
  /**
   * Grouped (no company picked): every company from the server with its user
   * count, not just the users loaded so far (DES 01-10-2026: "haan superadmin
   * pages pe bhi yehi kar do"), as on company Invoices. A group fetches its
   * own users when opened, 25 at a time. No stat cards here, so the flat
   * list is off while grouped.
   */
  const list = useUsersList(params, { enabled: !isGrouped });
  const summary = useUsersGroupSummary(params, isGrouped);
  // Open groups live in the URL (`open=key:batches,...`).
  const openParam = searchParams.get("open") ?? "";
  const openGroups = useMemo(() => {
    const map = new Map<string, number>();
    for (const part of openParam.split(",")) {
      const [key, n] = part.split(":");
      if (key) map.set(key, Math.max(1, Number(n) || 1));
    }
    return map;
  }, [openParam]);
  const setOpenGroups = (update: (prev: Map<string, number>) => Map<string, number>) => {
    const next = update(openGroups);
    const usp = new URLSearchParams(searchParams);
    const value = [...next].map(([k, n]) => (n > 1 ? `${k}:${n}` : k)).join(",");
    if (value) usp.set("open", value);
    else usp.delete("open");
    setSearchParams(usp, { replace: true });
  };
  // A search opens every group, so matches are visible.
  const searching = isGrouped && Boolean(params.search?.trim());
  const openList = useMemo(() => {
    const keys = searching
      ? (summary.data ?? []).map((g) => usersCompanyGroupKey(g.company_id))
      : [...openGroups.keys()];
    return keys.map((key) => ({ key, batches: openGroups.get(key) ?? 1 }));
  }, [searching, summary.data, openGroups]);
  const groupRows = useUsersGroupRows(params, openList);
  const groups = useMemo<TableRowGroup<AdminUser>[] | undefined>(() => {
    if (!isGrouped || !summary.data?.length) return undefined;
    return summary.data
      .map((g) => {
        const key = usersCompanyGroupKey(g.company_id);
        const idx = openList.findIndex((o) => o.key === key);
        const q = idx >= 0 ? groupRows[idx] : undefined;
        return {
          key,
          ...userCompanyGroupMeta({ company_name: g.company_name }),
          rows: q?.data?.users ?? [],
          total: g.count,
          loading: Boolean(q?.isFetching),
          error: Boolean(q?.isError),
          onRetry: () => void q?.refetch(),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [isGrouped, summary.data, openList, groupRows]);
  const usersRows = isGrouped
    ? (groups ?? []).flatMap((g) => g.rows)
    : list.data?.users ?? [];
  const pagination: PaginationState | undefined =
    !isGrouped && list.data?.pagination
      ? {
          page: list.data.pagination.page,
          limit: list.data.pagination.limit,
          total: list.data.pagination.total,
          totalPages: list.data.pagination.totalPages,
        }
      : undefined;

  // ----------------------------------------------------------------------
  // Filter / pagination handlers — push state into URL
  // ----------------------------------------------------------------------
  const setParams = (next: AdminUsersListParams) => {
    setSearchParams(searchFromParams(next), { replace: false });
  };

  const handlePageChange = (page: number) => setParams({ ...params, page });
  const handleLimitChange = (limit: number) =>
    setParams({ ...params, limit, page: 1 });

  // ----------------------------------------------------------------------
  // Column definitions
  // ----------------------------------------------------------------------
  const columns = useMemo<ColumnDef<AdminUser>[]>(
    () => [
      {
        id: "user",
        header: "User",
        cell: ({ row }) => {
          const u = row.original;
          const initials = getInitials(u.first_name, u.last_name, u.email);
          return (
            <div className="flex min-w-0 items-center gap-3">
              <div className="relative shrink-0">
                <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  <PersonPhoto src={u.profile_image_url} fallback={initials} />
                </div>
                {/* Flat (ungrouped) list has no timeline — keep mic on avatar. */}
                {!isGrouped ? (
                  <span className="absolute -bottom-0.5 -right-0.5">
                    <UserVoiceEnrollmentBadge
                      status={u.voice_enrollment_status}
                      size="sm"
                    />
                  </span>
                ) : null}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {formatFullName(u.first_name, u.last_name)}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {u.email}
                </p>
              </div>
            </div>
          );
        },
      },
      {
        id: "role",
        header: "Role",
        // "Internal" beside the role: our own account, left out of totals (18-10-2026).
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1">
            {/* The Owner account (B31): DES, protected everywhere. */}
            {row.original.is_owner ? <Badge variant="primary">Owner</Badge> : null}
            <UserRoleBadge role={row.original.role} />
            {row.original.is_internal ? <Badge variant="muted">Internal</Badge> : null}
            {row.original.two_factor_enabled ? (
              <Badge variant="success" title="Two-step verification is on">2FA</Badge>
            ) : null}
          </span>
        ),
      },
      {
        id: "billing",
        header: "Billing",
        cell: ({ row }) => (
          <UserBillingBadge
            planCode={row.original.subscription_plan_code}
            status={row.original.subscription_status}
            cancelAtPeriodEnd={row.original.subscription_cancel_at_period_end}
            periodEnd={row.original.subscription_period_end}
            usedMinutes={row.original.subscription_used_minutes}
            includedMinutes={row.original.subscription_included_minutes}
            remainingMinutes={row.original.subscription_remaining_minutes}
            enterpriseActive={row.original.enterprise_active}
            enterpriseSeated={row.original.enterprise_seated}
            enterpriseUsedMinutes={row.original.enterprise_used_minutes}
            enterpriseIncludedMinutes={
              row.original.enterprise_included_minutes
            }
            enterprisePeriodEnd={row.original.enterprise_period_end}
          />
        ),
      },
      {
        id: "native_language",
        header: "Native",
        cell: ({ row }) => (
          <UserNativeLanguageCell code={row.original.native_language} />
        ),
      },
      ...(isGrouped
        ? []
        : [
            {
              id: "company",
              header: "Company",
              cell: ({ row }) => {
                const u = row.original;
                if (u.company_name) {
                  return <span className="text-sm">{u.company_name}</span>;
                }
                if (u.company) {
                  return <span className="text-sm">{u.company}</span>;
                }
                return <span className="text-xs text-muted-foreground">—</span>;
              },
            } satisfies ColumnDef<AdminUser>,
          ]),
      {
        id: "sessions",
        header: "Sessions",
        cell: ({ row }) => (
          <span className="tabular-nums text-sm">
            {Number(row.original.session_count ?? 0).toLocaleString()}
          </span>
        ),
      },
      {
        id: "last_login_location",
        header: "Location",
        cell: ({ row }) => {
          const u = row.original;
          return (
            <LoginLocationPill
              country={u.last_login_country}
              region={u.last_login_region}
              city={u.last_login_city}
            />
          );
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <UserRowActions
              user={row.original}
              onEdit={(u) => setEditTarget(u)}
              onChangePassword={(u) => setPwTarget(u)}
              onViewHistory={(u) => setHistoryTarget(u)}
            />
          </div>
        ),
      },
    ],
    [isGrouped],
  );

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-72">
            <h2 className="text-xl font-semibold tracking-tight">Users</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Search, filter, and manage every user on the platform.
            </p>
          </div>
          {/* The filters, top right (DES 01-10-2026); New user is in the
              bottom dock. */}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <UsersHeaderFilters value={params} onChange={setParams} />
          </div>
        </GridBand>

        <GridCell padded={false} className="border-b-0">
          <DataTable<AdminUser, unknown>
            variant="cell"
            columns={columns}
            data={usersRows}
            groups={groups}
            expandAllGroups={searching}
            expandedGroupKeys={isGrouped ? [...openGroups.keys()] : undefined}
            onGroupExpandChange={(key, expanded) =>
              setOpenGroups((prev) => {
                const next = new Map(prev);
                if (expanded) next.set(key, next.get(key) ?? 1);
                else next.delete(key);
                return next;
              })
            }
            onGroupLoadMore={(key) =>
              setOpenGroups((prev) => new Map(prev).set(key, (prev.get(key) ?? 1) + 1))
            }
            groupNestedStyle={isGrouped ? "timeline" : "plain"}
            getTimelineMarkerClassName={(u) =>
              userStatusTimelineMarkerClass(u.status)
            }
            renderTimelineMarkerTrailing={(u) => (
              <UserVoiceEnrollmentBadge
                status={u.voice_enrollment_status}
                size="sm"
              />
            )}
            getRowTooltip={(u) => {
              const lastLoginRel = formatRelative(u.last_login_at);
              const lastLoginAbs = formatDateTime(u.last_login_at);
              const joinedRel = formatRelative(u.created_at);
              const joinedAbs = formatDateTime(u.created_at);
              return (
                <>
                  <p>
                    <span className="text-background/70">Last login · </span>
                    {lastLoginRel}
                    {lastLoginAbs !== "—" ? (
                      <span className="text-background/60">
                        {" "}
                        ({lastLoginAbs})
                      </span>
                    ) : null}
                  </p>
                  <p>
                    <span className="text-background/70">Registered · </span>
                    {joinedRel}
                    {joinedAbs !== "—" ? (
                      <span className="text-background/60">
                        {" "}
                        ({joinedAbs})
                      </span>
                    ) : null}
                  </p>
                </>
              );
            }}
            groupItemLabel="users"
            isLoading={isGrouped ? summary.isLoading : list.isLoading}
            isFetching={isGrouped ? summary.isFetching : list.isFetching}
            error={isGrouped ? summary.error : list.error}
            onRetry={() => (isGrouped ? summary.refetch() : list.refetch())}
            rowKey={(u) => u.id}
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            emptyShape="person"
            emptyTitle="No users match those filters"
            emptyDescription="Try clearing filters or adjusting your search."
            emptyAction={
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCreateOpen(true)}
                className="gap-2"
              >
                <UserRoundPlus className="h-4 w-4" />
                Create the first user
              </Button>
            }
          />
        </GridCell>
      </GridBlock>

      {/* The search, then the page's action, docked at the bottom like
          Notes (DES 01-10-2026). */}
      <PageDock>
        <UsersFilters value={params} onChange={setParams} />
        <DockActionButton icon={<Plus className="h-5 w-5" strokeWidth={2} />} onClick={() => setCreateOpen(true)}>
          New user
        </DockActionButton>
      </PageDock>

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditUserDialog
        user={editTarget}
        open={editTarget != null}
        onOpenChange={(open) => !open && setEditTarget(null)}
      />
      <ChangePasswordDialog
        user={pwTarget}
        open={pwTarget != null}
        onOpenChange={(open) => !open && setPwTarget(null)}
      />
      <LoginHistoryDialog
        user={historyTarget}
        open={historyTarget != null}
        onOpenChange={(open) => !open && setHistoryTarget(null)}
      />

      <UsersIcon className="hidden" aria-hidden />
    </div>
  );
}
